import type { ImportPreviewRow } from '@/lib/api-client';

interface ImportPreviewTableProps {
  rows: ImportPreviewRow[];
}

export function ImportPreviewTable({ rows }: ImportPreviewTableProps) {
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-[var(--fg-muted)]">
        Nenhuma linha disponível para prévia.
      </p>
    );
  }

  // Coleta todas as chaves únicas dos mappedData para montar colunas dinamicamente
  const allKeys = Array.from(
    new Set(
      rows.flatMap((r) =>
        r.mappedData ? Object.keys(r.mappedData) : [],
      ),
    ),
  ).slice(0, 8); // limita a 8 colunas para não explodir a tabela

  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
      <table className="w-full text-xs">
        <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left uppercase tracking-wide text-[var(--fg-muted)]">
          <tr>
            <th className="px-3 py-2 font-medium w-12">#</th>
            {allKeys.map((k) => (
              <th key={k} className="px-3 py-2 font-medium">
                {k}
              </th>
            ))}
            <th className="px-3 py-2 font-medium">Erros / Avisos</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hasError = row.errors.length > 0;
            const hasWarning = row.warnings.length > 0;
            return (
              <tr
                key={row.rowIndex}
                className={[
                  'border-t border-[var(--border)]',
                  hasError
                    ? 'bg-red-500/5'
                    : hasWarning
                      ? 'bg-amber-500/5'
                      : '',
                ].join(' ')}
              >
                <td className="px-3 py-2 text-[var(--fg-muted)]">
                  {row.rowIndex}
                </td>
                {allKeys.map((k) => (
                  <td key={k} className="px-3 py-2 text-[var(--fg)]">
                    {row.mappedData
                      ? String(row.mappedData[k] ?? '')
                      : '—'}
                  </td>
                ))}
                <td className="px-3 py-2">
                  {hasError && (
                    <ul className="space-y-0.5">
                      {row.errors.map((e, i) => (
                        <li key={i} className="text-red-400">
                          {e}
                        </li>
                      ))}
                    </ul>
                  )}
                  {hasWarning && (
                    <ul className="space-y-0.5">
                      {row.warnings.map((w, i) => (
                        <li key={i} className="text-amber-400">
                          {w}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!hasError && !hasWarning && (
                    <span className="text-emerald-500">OK</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
